// Slug-keyed registry for /areas/$area dynamic route
// Each entry combines tourist zone + relevant services + nearby segments

export interface PhuketAreaPage {
  slug: string;
  nameTh: string;
  nameEn: string;
  district: "Mueang Phuket" | "Kathu" | "Thalang";
  districtTh: string;
  type: "beach" | "marina" | "old-town" | "business";
  postalCode: string;
  metaTitle: string;
  metaDesc: string;
  intro: string;
  highlights: string[];
  topServices: string[]; // service slugs
  topSegments: string[]; // segment slugs
  nearbyOffices: string[]; // branch ids
  travelFee: string;
  landmark: string;
}

export const PHUKET_AREA_PAGES: PhuketAreaPage[] = [
  {
    slug: "patong",
    nameTh: "ป่าตอง",
    nameEn: "Patong",
    district: "Kathu",
    districtTh: "อำเภอกะทู้",
    type: "beach",
    postalCode: "83150",
    metaTitle: "Notary Public Patong Phuket | Thai Notary",
    metaDesc: "Notary Public, document translation and visa service in Patong, Phuket. On-site at your hotel, condo or villa along Bangla Rd, Jungceylon, Kalim. Open daily.",
    intro: "ป่าตองเป็นศูนย์กลางการท่องเที่ยวและไนท์ไลฟ์ของภูเก็ต — มีโรงแรม คอนโด และร้านอาหารกว่าพันแห่ง ทีม Thai Notary Phuket มีจุดบริการประจำที่ป่าตอง รับงาน Notary, แปลและรับรองเอกสารสำหรับ Expat, นักท่องเที่ยวระยะยาว และเจ้าของกิจการ on-site ถึงโรงแรม/คอนโดของคุณตลอด Bangla Rd, Jungceylon, Kalim, Tri Trang",
    highlights: ["ใกล้ Jungceylon, Bangla Road", "ครอบคลุม Kalim, Tri Trang, Paradise Beach", "ทีมพูด Russian / Chinese / English / Deutsch"],
    topServices: ["notary-en", "translate-all", "thai-visa", "marriage-th", "wp"],
    topSegments: ["expats", "digital-nomads", "business-owners", "marriage"],
    nearbyOffices: ["patong"],
    travelFee: "ฟรี (ในเขตป่าตอง)",
    landmark: "Jungceylon Shopping Mall",
  },
  {
    slug: "kata",
    nameTh: "กะตะ",
    nameEn: "Kata",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "beach",
    postalCode: "83100",
    metaTitle: "Notary Kata Beach Phuket | Villa & Visa | Thai Notary",
    metaDesc: "Notary, translation and property POA service in Kata Beach. Long-stay expats, Russian community, surf school owners — on-site at your villa or condo.",
    intro: "กะตะมีชุมชน Expat ที่อยู่ระยะยาว — ส่วนใหญ่เป็นรัสเซีย ออสเตรเลีย เยอรมัน ฝรั่งเศส ที่ซื้อคอนโดบนเขา หรือเช่าวิลล่าระยะยาว เราเชี่ยวชาญงาน POA วิลล่า, ต่อ Retirement Visa, รับรองเอกสารโรงเรียนเซิร์ฟและธุรกิจร้านอาหาร on-site",
    highlights: ["ชุมชน Russian/Australian expat", "ใกล้ Kata Noi, Karon", "เชี่ยวชาญ Property POA วิลล่าบนเขา"],
    topServices: ["notary-en", "translate-all", "thai-visa", "marriage-th"],
    topSegments: ["expats", "property-buyers", "retirees", "business-owners"],
    nearbyOffices: ["phuket-town"],
    travelFee: "500 บาท",
    landmark: "Kata Beach / Kata Noi",
  },
  {
    slug: "karon",
    nameTh: "กะรน",
    nameEn: "Karon",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "beach",
    postalCode: "83100",
    metaTitle: "Notary Karon Beach Phuket | RU/SE/FI | Thai Notary",
    metaDesc: "Russian, Swedish, Finnish-speaking notary and visa service in Karon Beach. Property, retirement, marriage paperwork — on-site at hotels, condos, villas.",
    intro: "กะรนเป็นชุมชน Expat ขนาดใหญ่ที่สุดของรัสเซียและสแกนดิเนเวียในภูเก็ต — เราให้บริการเป็นภาษารัสเซีย สวีเดน ฟินแลนด์ ผ่านกงสุลกิตติมศักดิ์ในเกาะ ครอบคลุมเอกสาร Property POA, Retirement Visa, จดทะเบียนสมรส on-site",
    highlights: ["Russian-speaking team", "Honorary Consul Russia/Sweden/Finland ในเกาะ", "ใกล้ Karon Viewpoint, Kata"],
    topServices: ["notary-en", "translate-all", "marriage-th", "thai-visa"],
    topSegments: ["expats", "retirees", "marriage", "property-buyers"],
    nearbyOffices: ["phuket-town"],
    travelFee: "500 บาท",
    landmark: "Karon Beach",
  },
  {
    slug: "rawai",
    nameTh: "ราไวย์",
    nameEn: "Rawai",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "beach",
    postalCode: "83130",
    metaTitle: "Notary Rawai Phuket | Retirees & Yacht | Thai Notary",
    metaDesc: "Notary, translation, retirement visa and yacht document service in Rawai, Nai Harn, Chalong. Long-term expat community — on-site at home or marina.",
    intro: "ราไวย์ ใน หาน และฉลอง เป็นพื้นที่ของผู้เกษียณและลูกเรือยอตช์เป็นหลัก — เราเชี่ยวชาญ Retirement Visa, Thai Will, Seafarer Documents และ Yacht Charter Notary on-site ที่บ้านหรือเรือ",
    highlights: ["ชุมชน Retiree (German, French, Australian)", "ใกล้ Chalong Bay Marina", "เชี่ยวชาญ Yacht / Seafarer Notary"],
    topServices: ["notary-en", "translate-all", "thai-visa", "police-intl"],
    topSegments: ["retirees", "yacht-crew", "expats"],
    nearbyOffices: ["chalong", "phuket-town"],
    travelFee: "500 บาท",
    landmark: "Nai Harn Beach / Promthep Cape",
  },
  {
    slug: "kamala",
    nameTh: "กมลา",
    nameEn: "Kamala",
    district: "Kathu",
    districtTh: "อำเภอกะทู้",
    type: "beach",
    postalCode: "83150",
    metaTitle: "Notary Kamala Millionaire's Mile | Thai Notary Phuket",
    metaDesc: "Premium notary and POA service in Kamala — Millionaire's Mile villas, Cape Amarin, Andara, The Plantation. High-net-worth property and trust paperwork.",
    intro: "กมลา (Millionaire's Mile) เป็นย่านวิลล่าระดับ Ultra-luxury ของภูเก็ต — เราให้บริการเฉพาะกลุ่ม High-net-worth สำหรับ Property POA, Trust Structuring, LTR Wealthy Global Citizen Visa, Estate Planning ครบจบที่วิลล่า",
    highlights: ["Millionaire's Mile", "ใกล้ Cape Amarin, Andara", "Premium / High-net-worth focus"],
    topServices: ["notary-en", "translate-all", "embassy", "thai-visa"],
    topSegments: ["property-buyers", "expats", "retirees"],
    nearbyOffices: ["patong", "cherngtalay"],
    travelFee: "500 บาท",
    landmark: "Kamala Beach / Millionaire's Mile",
  },
  {
    slug: "surin",
    nameTh: "สุรินทร์",
    nameEn: "Surin",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "beach",
    postalCode: "83110",
    metaTitle: "Notary Surin Beach Phuket | Luxury Villas | Thai Notary",
    metaDesc: "Notary and legal service in Surin Beach — Catch Beach Club, Twinpalms, Pansea. Luxury villa POA, business setup for hospitality.",
    intro: "สุรินทร์เป็นย่านวิลล่าและ Beach Club ระดับลักซ์ชัวรี — ลูกค้าหลักเป็นเจ้าของวิลล่า, ผู้ลงทุน Hospitality และครอบครัว Expat ระยะยาว เราดูแลทุกเอกสารตั้งแต่ POA วิลล่าถึงใบอนุญาตโรงแรมและ Beach Club",
    highlights: ["Twinpalms, Catch Beach Club", "Luxury villa estates", "Business owner focus"],
    topServices: ["notary-en", "translate-all", "wp", "thai-visa"],
    topSegments: ["property-buyers", "business-owners", "expats"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "500 บาท",
    landmark: "Surin Beach / Pansea",
  },
  {
    slug: "bang-tao",
    nameTh: "บางเทา",
    nameEn: "Bang Tao",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "beach",
    postalCode: "83110",
    metaTitle: "Notary Bang Tao Laguna Phuket | Thai Notary",
    metaDesc: "Notary, condo POA, business setup in Bang Tao — Laguna Phuket, Boat Avenue, Porto de Phuket. Largest expat business community on the island.",
    intro: "บางเทา–ลากูน่าเป็นย่านพักอาศัยและธุรกิจของ Expat ที่ใหญ่ที่สุดในภูเก็ต — ครอบคลุม Laguna Phuket, Boat Avenue, Porto de Phuket, Cherngtalay เราให้บริการ POA คอนโด/วิลล่า, ตั้งบริษัท, จดทะเบียนสมรสที่อำเภอถลาง",
    highlights: ["Laguna Phuket • Boat Avenue • Porto de Phuket", "อำเภอถลาง (จดทะเบียนสมรส)", "ใหญ่ที่สุดของ Expat business community"],
    topServices: ["notary-en", "translate-all", "marriage-th", "wp"],
    topSegments: ["expats", "property-buyers", "marriage", "business-owners", "digital-nomads"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "ฟรี (ในเขตเชิงทะเล)",
    landmark: "Laguna Phuket / Boat Avenue",
  },
  {
    slug: "cherngtalay",
    nameTh: "เชิงทะเล",
    nameEn: "Cherngtalay",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "business",
    postalCode: "83110",
    metaTitle: "Notary Cherngtalay Laguna Phuket | Thai Notary",
    metaDesc: "Office-based notary and legal service in Cherngtalay (Laguna). Marriage registration at Thalang District Office, company setup, work permit.",
    intro: "เชิงทะเลเป็นที่ตั้งสำนักงานสาขาลากูน่าของเรา — รับงาน walk-in ทุกวัน 08:00–20:00 ครอบคลุม Laguna, Bang Tao, Layan, Cherngtalay พร้อมประสานอำเภอถลางสำหรับจดทะเบียนสมรส",
    highlights: ["สำนักงานสาขาประจำ", "อำเภอถลางใกล้ที่สุด", "Walk-in 7 วัน/สัปดาห์"],
    topServices: ["notary-en", "translate-all", "marriage-th", "thai-visa", "wp"],
    topSegments: ["expats", "marriage", "property-buyers", "business-owners"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "ฟรี (walk-in)",
    landmark: "Laguna Phuket Roundabout",
  },
  {
    slug: "nai-yang",
    nameTh: "ในยาง",
    nameEn: "Nai Yang",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "beach",
    postalCode: "83110",
    metaTitle: "Notary Nai Yang Airport Phuket | Thai Notary",
    metaDesc: "Notary and document service in Nai Yang Beach near Phuket International Airport. Last-minute document pickup before flight, airline crew notary.",
    intro: "ในยางอยู่ใกล้สนามบินภูเก็ต — เหมาะสำหรับลูกค้าที่ต้องรับเอกสารก่อนบิน หรือลูกเรือสายการบินที่ต้องการ Notary on-site ที่โรงแรม Indigo Pearl, Marriott, Anantara Mai Khao",
    highlights: ["ใกล้สนามบินภูเก็ต (HKT)", "Same-day pickup ก่อนบิน", "Airline crew service"],
    topServices: ["notary-en", "translate-all", "police-intl"],
    topSegments: ["expats", "digital-nomads", "business-owners"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "800 บาท",
    landmark: "Nai Yang Beach / Sirinat NP",
  },
  {
    slug: "mai-khao",
    nameTh: "ไม้ขาว",
    nameEn: "Mai Khao",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "beach",
    postalCode: "83110",
    metaTitle: "Notary Mai Khao Luxury Resorts | Thai Notary Phuket",
    metaDesc: "Premium notary service for Mai Khao luxury resort belt — Anantara, Marriott, JW Marriott, Renaissance. Resort guest concierge notary.",
    intro: "ไม้ขาวเป็นย่านรีสอร์ทระดับ 5 ดาว — Anantara, JW Marriott, Renaissance, Marriott — เรารับเชิญจาก Concierge มาทำ Notary ที่ห้องพักหรือบิสซิเนสเซ็นเตอร์",
    highlights: ["Luxury resort belt", "Concierge partnership", "ใกล้สนามบิน + ไม้ขาว"],
    topServices: ["notary-en", "translate-all", "embassy", "thai-visa"],
    topSegments: ["expats", "business-owners", "property-buyers"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "1,000 บาท",
    landmark: "Anantara Mai Khao / JW Marriott",
  },
  {
    slug: "phuket-old-town",
    nameTh: "เมืองเก่าภูเก็ต",
    nameEn: "Phuket Old Town",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "old-town",
    postalCode: "83000",
    metaTitle: "Notary Phuket Old Town Head Office | Thai Notary",
    metaDesc: "Head office in Phuket Old Town — walk-in 7 days near Provincial Hall, Land Office and Immigration. Full government coordination.",
    intro: "เมืองเก่าภูเก็ตเป็นที่ตั้งสำนักงานใหญ่ของเรา ใกล้ศาลากลาง สำนักงานที่ดิน ตม. ภูเก็ต และศาลจังหวัด — เหมาะกับงานที่ต้องประสานราชการครบทุกหน่วย",
    highlights: ["สำนักงานใหญ่", "ใกล้ศาลากลาง + ที่ดิน + ตม.", "Walk-in 7 วัน 08:00–20:00"],
    topServices: ["notary-en", "translate-all", "thai-visa", "wp", "marriage-th", "embassy"],
    topSegments: ["expats", "digital-nomads", "business-owners", "marriage", "students"],
    nearbyOffices: ["phuket-town"],
    travelFee: "ฟรี (walk-in)",
    landmark: "Thalang Road / Soi Romanee",
  },
  {
    slug: "chalong",
    nameTh: "อ่าวฉลอง",
    nameEn: "Chalong Bay",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "marina",
    postalCode: "83130",
    metaTitle: "Yacht Notary Chalong Bay Phuket | Thai Notary",
    metaDesc: "Yacht notary, charter agreement, seafarer documents at Chalong Bay — Phuket's primary yacht pier. On-site at boat or marina office.",
    intro: "อ่าวฉลองเป็นท่าเทียบเรือยอตช์หลักของภูเก็ต — เราให้บริการ Notary on-site บนเรือ, Charter Agreement, Seafarer Documents, ตม. Chalong (Crew Visa)",
    highlights: ["Phuket's main yacht pier", "Crew Visa via ตม. Chalong", "Bareboat Charter notary"],
    topServices: ["notary-en", "translate-all", "thai-visa", "police-intl"],
    topSegments: ["yacht-crew", "retirees", "business-owners"],
    nearbyOffices: ["chalong"],
    travelFee: "ฟรี (walk-in)",
    landmark: "Chalong Pier",
  },
  {
    slug: "ao-por",
    nameTh: "อ่าวปอ",
    nameEn: "Ao Por",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "marina",
    postalCode: "83110",
    metaTitle: "Yacht Notary Ao Po Marina Phuket | Thai Notary",
    metaDesc: "On-site yacht notary at Ao Po Grand Marina — superyacht charter, crew contracts, Bareboat agreements. Quiet east-coast marina.",
    intro: "อ่าวปอ Grand Marina เป็นมารีน่าสำหรับ Superyacht ฝั่งตะวันออกของเกาะ — เราให้บริการ Notary บนเรือ พร้อมประสาน Royal Thai Marine Police สำหรับ Crew Clearance",
    highlights: ["Superyacht-focused", "East coast (calm waters)", "Marine Police coordination"],
    topServices: ["notary-en", "translate-all", "embassy", "police-intl"],
    topSegments: ["yacht-crew", "business-owners"],
    nearbyOffices: ["phuket-town"],
    travelFee: "1,200 บาท",
    landmark: "Ao Po Grand Marina",
  },
  {
    slug: "royal-phuket-marina",
    nameTh: "Royal Phuket Marina",
    nameEn: "Royal Phuket Marina",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "marina",
    postalCode: "83000",
    metaTitle: "Notary Royal Phuket Marina | Thai Notary",
    metaDesc: "Yacht charter notary, condo POA at Royal Phuket Marina — mixed-use marina with luxury condos and superyacht berths. ทีมงานพร้อมให้บริการ on-site ทั่วเกาะภูเก็",
    intro: "Royal Phuket Marina เป็นมารีน่า + คอนโดลักซ์ชัวรี — เราให้บริการ Yacht Notary + Property POA สำหรับเจ้าของคอนโดที่นี่",
    highlights: ["Mixed marina + condo", "Yacht + property combined", "ใกล้ Phuket Town"],
    topServices: ["notary-en", "translate-all", "embassy"],
    topSegments: ["yacht-crew", "property-buyers"],
    nearbyOffices: ["phuket-town"],
    travelFee: "500 บาท",
    landmark: "Royal Phuket Marina",
  },
  {
    slug: "boat-lagoon",
    nameTh: "Boat Lagoon",
    nameEn: "Boat Lagoon Marina",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "marina",
    postalCode: "83000",
    metaTitle: "Notary Boat Lagoon Marina Phuket | Thai Notary",
    metaDesc: "Yacht notary at Boat Lagoon Marina — Phuket's longest-running marina with full boatyard services and large expat yachting community.",
    intro: "Boat Lagoon เป็นมารีน่าที่เก่าแก่ที่สุดของภูเก็ต — ทีมเรารู้จักทุก Captain และ Yacht Broker ในมารีน่า ให้บริการ Notary, แปล, Crew Visa",
    highlights: ["Phuket's oldest marina", "Full boatyard + brokerage", "Strong Captain network"],
    topServices: ["notary-en", "translate-all", "thai-visa", "police-intl"],
    topSegments: ["yacht-crew", "business-owners"],
    nearbyOffices: ["phuket-town"],
    travelFee: "500 บาท",
    landmark: "Boat Lagoon Marina",
  },
  {
    slug: "yacht-haven",
    nameTh: "Yacht Haven",
    nameEn: "Yacht Haven Marina",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "marina",
    postalCode: "83110",
    metaTitle: "Notary Yacht Haven Marina Phuket | Thai Notary",
    metaDesc: "Superyacht notary at Yacht Haven Marina — northeast Phuket, deep-water berths, Phuket Yacht Show venue. ทีมงานพร้อมให้บริการ on-site ทั่วเกาะภูเก็ต — ติดต่อสอ",
    intro: "Yacht Haven Marina เป็น Superyacht hub ของภูเก็ตและสถานที่จัด Phuket Yacht Show — เราให้บริการ Notary บนเรือใหญ่ พร้อม Marine Insurance, Charter, Crew documents",
    highlights: ["Superyacht deep-water berths", "Phuket Yacht Show venue", "Marine insurance partnerships"],
    topServices: ["notary-en", "translate-all", "embassy", "police-intl"],
    topSegments: ["yacht-crew", "business-owners", "property-buyers"],
    nearbyOffices: ["phuket-town", "cherngtalay"],
    travelFee: "1,000 บาท",
    landmark: "Yacht Haven Marina, Laem Hin",
  },
  {
    slug: "wichit",
    nameTh: "วิชิต",
    nameEn: "Wichit",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "business",
    postalCode: "83000",
    metaTitle: "Notary วิชิต ภูเก็ต | แปลเอกสาร + กงสุล | Thai Notary Phuket",
    metaDesc: "บริการ Notary Public แปลเอกสาร รับรองกงสุล MFA สถานทูต ในตำบลวิชิต ภูเก็ต ครอบคลุมโครงการบ้านจัดสรร คอนโดมิเนียม และสำนักงาน on-site ถึงที่",
    intro: "วิชิตเป็นพื้นที่ที่อยู่อาศัยใหญ่ที่สุดในเขตเทศบาลเมืองภูเก็ต ใกล้สนามบิน Phuket Town และทางออกไปสะพานสารสิน เต็มไปด้วยบ้านจัดสรร คอนโดมิเนียม และสำนักงานบริษัทขนาดกลาง ทีมเราให้บริการ Notary, แปลเอกสาร, รับรองกงสุล และวีซ่า on-site ถึงบ้าน/คอนโด/ออฟฟิศได้ทันที",
    highlights: ["ใกล้ Lotus's, Tesco, Central Phuket Floresta", "ครอบคลุมโครงการ Land & Houses, Supalai, Sansiri", "นักลงทุนอสังหา/เจ้าของกิจการ SME ภูเก็ต"],
    topServices: ["notary-th", "translate-gov", "mfa-th", "wp", "police-th"],
    topSegments: ["business-owners", "property-buyers", "expats"],
    nearbyOffices: ["phuket-town"],
    travelFee: "ฟรี (ในเขตวิชิต)",
    landmark: "Central Phuket Floresta",
  },
  {
    slug: "koh-kaew",
    nameTh: "เกาะแก้ว",
    nameEn: "Koh Kaew",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "business",
    postalCode: "83000",
    metaTitle: "Notary เกาะแก้ว ภูเก็ต | British International School Area",
    metaDesc: "บริการ Notary Public แปลเอกสารโรงเรียนนานาชาติ วีซ่าครอบครัว ในตำบลเกาะแก้ว ภูเก็ต ใกล้ British International School และ Boat Lagoon Marina",
    intro: "เกาะแก้วเป็นย่านที่อยู่อาศัยของครอบครัว Expat ที่ส่งบุตรเรียน British International School Phuket (BISP) มีโครงการบ้านระดับ Premium และคอนโดสำหรับครอบครัว ทีมเราเชี่ยวชาญงานแปลใบเกิด-วุฒิ-Transcript สำหรับสมัครเรียน, วีซ่าครอบครัวติดตาม และ Notary POA สำหรับซื้อบ้าน",
    highlights: ["ใกล้ British International School Phuket", "ติดกับ Boat Lagoon Marina", "ชุมชนครอบครัว Expat อังกฤษ ออสเตรเลีย"],
    topServices: ["notary-en", "translate-all", "thai-visa", "marriage-th", "visa-foreign"],
    topSegments: ["expats", "marriage", "property-buyers", "business-owners"],
    nearbyOffices: ["phuket-town"],
    travelFee: "300 บาท",
    landmark: "British International School Phuket",
  },
  {
    slug: "talad-yai",
    nameTh: "ตลาดใหญ่",
    nameEn: "Talad Yai",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "old-town",
    postalCode: "83000",
    metaTitle: "Notary ตลาดใหญ่ ภูเก็ต | ใจกลางเมือง | Thai Notary",
    metaDesc: "Notary Public, แปลเอกสาร, รับรองกงสุล MFA สถานทูต ในย่านตลาดใหญ่ ใจกลางเมืองภูเก็ต ใกล้ศาลากลางจังหวัด ที่ทำการอำเภอ และสำนักงานที่ดิน",
    intro: "ตลาดใหญ่เป็นใจกลางการปกครองและธุรกิจของเมืองภูเก็ต ใกล้ศาลากลางจังหวัด ที่ว่าการอำเภอเมือง สำนักงานที่ดิน สำนักงานพาณิชย์จังหวัด และศาลจังหวัดภูเก็ต ทีมเราใช้พื้นที่นี้เป็นฐานในการประสานงานเอกสารราชการให้ลูกค้าทั่วเกาะ",
    highlights: ["ใกล้ศาลากลาง อำเภอเมือง ที่ดินจังหวัด", "เดินถึงศาลจังหวัดภูเก็ต", "ติด Phuket Old Town"],
    topServices: ["notary-th", "translate-gov", "moj", "mfa-th", "cert-gov-th"],
    topSegments: ["business-owners", "marriage", "expats"],
    nearbyOffices: ["phuket-town"],
    travelFee: "ฟรี",
    landmark: "ศาลากลางจังหวัดภูเก็ต",
  },
  {
    slug: "sakhu",
    nameTh: "สาคู",
    nameEn: "Sakhu",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "business",
    postalCode: "83110",
    metaTitle: "Notary สาคู ภูเก็ต | ใกล้สนามบิน | Thai Notary",
    metaDesc: "Notary Public, แปลเอกสาร, เซ็นเอกสารด่วนใกล้สนามบินภูเก็ต (HKT) บริการ Last-minute สำหรับนักเดินทาง เจ้าของกิจการขนส่ง และ Expat ที่อยู่ไม้ขาว-ในยาง",
    intro: "สาคูคือตำบลที่ตั้งสนามบินนานาชาติภูเก็ต (HKT) เป็นจุดผ่านของผู้โดยสารระหว่างประเทศหลายหมื่นคนต่อวัน ทีมเรามีจุดบริการ Last-minute Notary สำหรับลูกค้าที่ต้องเซ็นเอกสารก่อนขึ้นเครื่อง พร้อมบริการแปลเอกสารด่วนภายใน 2 ชั่วโมง",
    highlights: ["ห่างสนามบินภูเก็ต 5 นาที", "บริการ Last-minute สำหรับ Flight", "ใกล้ Cargo Terminal สำหรับธุรกิจขนส่ง"],
    topServices: ["notary-en", "translate-all", "naati", "police-intl"],
    topSegments: ["business-owners", "expats", "digital-nomads"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "300 บาท",
    landmark: "Phuket International Airport (HKT)",
  },
  {
    slug: "pa-khlok",
    nameTh: "ป่าคลอก",
    nameEn: "Pa Khlok",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "marina",
    postalCode: "83110",
    metaTitle: "Notary ป่าคลอก ภูเก็ต | Ao Po Grand Marina | Thai Notary",
    metaDesc: "Notary Public และบริการเอกสารเรือยอชต์ที่ตำบลป่าคลอก ใกล้ Ao Po Grand Marina และ East Coast Marinas เชี่ยวชาญ Crew Visa, Yacht Documentation, Marine Insurance",
    intro: "ป่าคลอกเป็นชายฝั่งตะวันออกเฉียงเหนือของภูเก็ตที่ตั้งของ Ao Po Grand Marina ซึ่งเป็นมารีน่า Superyacht ระดับโลก ทีมเราดูแลเอกสาร Captain, Crew Work Permit, Yacht Registration, Charter Agreement และ Notary on-board ให้ลูกค้าในพื้นที่นี้เป็นประจำ",
    highlights: ["Ao Po Grand Marina – Superyacht hub", "ใกล้ Yacht Haven และ Royal Phuket Marina", "เชี่ยวชาญเอกสาร Marine ทุกประเภท"],
    topServices: ["notary-en", "translate-all", "embassy", "police-intl", "wp"],
    topSegments: ["yacht-crew", "business-owners", "expats"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "1,000 บาท",
    landmark: "Ao Po Grand Marina",
  },
  {
    slug: "thep-krasattri",
    nameTh: "เทพกระษัตรี",
    nameEn: "Thep Krasattri",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "business",
    postalCode: "83110",
    metaTitle: "Notary เทพกระษัตรี ถลาง ภูเก็ต | Thai Notary",
    metaDesc: "Notary Public, แปลเอกสาร, รับรองกงสุล และวีซ่าในตำบลเทพกระษัตรี อำเภอถลาง ภูเก็ต ครอบคลุม Boat Avenue, Porto de Phuket, Laguna",
    intro: "เทพกระษัตรีเป็นตำบลใหญ่ของอำเภอถลาง ครอบคลุมพื้นที่ Boat Avenue, Porto de Phuket และเชื่อมต่อ Laguna Phuket ทีมเรามีจุดบริการที่นี่เพื่อดูแลครอบครัว Expat ที่ส่งบุตรเรียน UWC Thailand, HeadStart International School และเจ้าของวิลล่าในโซน Cherngtalay-Thalang",
    highlights: ["ใกล้ Boat Avenue, Porto de Phuket", "UWC Thailand, HeadStart International School", "เชื่อมต่อ Laguna และ Bang Tao"],
    topServices: ["notary-en", "translate-all", "marriage-th", "thai-visa", "translate-visa"],
    topSegments: ["expats", "property-buyers", "marriage", "retirees"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "500 บาท",
    landmark: "Boat Avenue / Porto de Phuket",
  },
  {
    slug: "cape-panwa",
    nameTh: "แหลมพันวา",
    nameEn: "Cape Panwa",
    district: "Mueang Phuket",
    districtTh: "อำเภอเมืองภูเก็ต",
    type: "beach",
    postalCode: "83000",
    metaTitle: "Notary แหลมพันวา ภูเก็ต | Resort & Marine | Thai Notary",
    metaDesc: "Notary Public, แปลเอกสาร, รับรองวีซ่าที่แหลมพันวา ภูเก็ต ใกล้ Cape Panwa Hotel, Aquarium, Deep Sea Port และชุมชนวิจัยทางทะเล",
    intro: "แหลมพันวาเป็นแหลมตะวันออกของภูเก็ต ที่ตั้งของ Phuket Aquarium, Cape Panwa Hotel, Deep Sea Port และศูนย์วิจัยพันธุ์สัตว์น้ำชายฝั่ง ทีมเราดูแลลูกค้ากลุ่มนักวิจัยต่างชาติ พนักงานท่าเรือน้ำลึก และเจ้าของวิลล่าระดับ Premium ในโซน Panwa-Sapam",
    highlights: ["Phuket Aquarium / Marine Biology Center", "Cape Panwa Hotel + Deep Sea Port", "วิลล่า Premium มุมมองทะเล"],
    topServices: ["notary-en", "translate-all", "thai-visa", "wp", "embassy"],
    topSegments: ["expats", "property-buyers", "business-owners", "retirees"],
    nearbyOffices: ["phuket-town"],
    travelFee: "500 บาท",
    landmark: "Phuket Aquarium / Cape Panwa",
  },
  {
    slug: "koh-naka",
    nameTh: "เกาะนาคา",
    nameEn: "Koh Naka",
    district: "Thalang",
    districtTh: "อำเภอถลาง",
    type: "marina",
    postalCode: "83110",
    metaTitle: "Notary เกาะนาคา / Naka Yai | Thai Notary",
    metaDesc: "Notary Public on-board สำหรับเจ้าของวิลล่าและรีสอร์ทบนเกาะนาคาใหญ่-นาคาน้อย ภูเก็ต บริการเอกสาร Yacht, POA, Charter ส่งถึงที่",
    intro: "เกาะนาคาใหญ่และเกาะนาคาน้อยเป็นเกาะส่วนตัวที่ตั้งของรีสอร์ทระดับ Ultra-luxury เช่น Naka Island Resort และวิลล่าส่วนตัวของนักธุรกิจระดับโลก ทีมเราให้บริการ Notary on-board และนัดทีมเดินทางพร้อมเรือไปบนเกาะเพื่อเซ็นเอกสาร POA, Charter, Marine Insurance",
    highlights: ["Naka Island Luxury Resort", "Notary on-board ส่งถึงเกาะ", "บริการ POA วิลล่าระดับ Ultra-luxury"],
    topServices: ["notary-en", "translate-all", "embassy", "marriage-th"],
    topSegments: ["yacht-crew", "property-buyers", "business-owners"],
    nearbyOffices: ["cherngtalay"],
    travelFee: "ติดต่อสอบถาม (รวมค่าเรือ)",
    landmark: "Naka Yai Island",
  },
];

export const TOTAL_AREA_PAGES = PHUKET_AREA_PAGES.length;

export function getAreaPage(slug: string) {
  return PHUKET_AREA_PAGES.find((a) => a.slug === slug);
}